import { Button, ControlledComponentProps, ErrorMessages, Flex, Group, Icon, PillGroup, Stack } from "@/components"
import { useStyles } from "@/components/Controlled/EditableSearchify/EditableSearchify.styles"
import { QuorumDataTypesWithValue, SafedSearch } from "@/types/djangio"
import React, { useEffect, useState } from "react"
import { useController, useFormContext } from "react-hook-form"
import swal from "sweetalert"
import { createSafedSearchObject } from "@/services/campaigns"
import { advancedSearchify } from "app/static/frontend/search-new/searchify/searchify-action-creators"
import { connect } from "react-redux"
import { getFLVFromQDT } from "app/static/frontend/search-new/routes"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/api"
import { Loader } from "@mantine/core"
import { EditOfficials } from "@/pages/campaign-form/components/sections/message/EditOfficials"

interface SearchifyProps {
    name: string
    dataType: QuorumDataTypesWithValue
    openSearchify?: (object) => void
    buttonText: string
    defaultFilters?: object
    customEdit?: boolean
}

const openSearchify =
    ({ dataType, onChange, searchifyHeaderName, searchifyInitialFilters, defaultFilters }) =>
    (dispatch) => {
        dispatch(
            advancedSearchify({
                searchifyFLV: getFLVFromQDT(dataType.value),
                searchifyQDT: dataType,
                searchifyInitialFilters,
                searchifyPermanentFilter: defaultFilters,
                shouldOpenSearchifyModal: true,
                maxSearchifySelections: Infinity,
                searchifyHeaderName,
                callBack: onChange,
            }),
        )
    }

export const openSelectionNamingModal = async (editedListName?: string) => {
    const placeholder = "Name your selection here"
    const attributes = editedListName ? { value: editedListName } : { placeholder }
    return swal({
        buttons: ["Cancel", "Save"],
        title: placeholder,
        content: {
            element: "input",
            attributes,
        },
    }).then((inputValue) => {
        if (inputValue === "") {
            return editedListName ?? "Unnamed List"
        }
        return inputValue
    })
}

const fetchEstimatedSizes = async ({ queryKey }) => {
    const safedSearchIds = queryKey[1]
    const response = await api.get({
        model: DjangIO.app.userdata.models.SafedSearch,
        params: `ids=${safedSearchIds}`,
        action: "get_estimated_size",
    })

    return response.json()
}

const DisconnectedEditableSearchify = ({
    name,
    dataType,
    rules,
    buttonText,
    openSearchify,
    defaultFilters,
    customEdit = false,
}: ControlledComponentProps<SearchifyProps>) => {
    const [isEditOfficialOpen, setEditOfficialOpen] = useState<boolean>(false)
    const [getPillGroupIndex, setPillGroupIndex] = useState<number>(0)
    const [getPillGroup, setPillGroup] = useState<SafedSearch>(null)
    const { control, trigger } = useFormContext()
    const { field, fieldState } = useController({ control, name, rules })
    const { classes } = useStyles()
    const safedSearchIds = field.value.map((search: SafedSearch) => search.id)

    const onChangeIntercept = (value: SafedSearch[]) => {
        field.onChange(value)
        trigger(name)
    }

    const onChangeSearchify = async ({ searchifyFilters }, index?: number | undefined, title?: string) => {
        if (!searchifyFilters.searchifyCount) return
        const listName = await openSelectionNamingModal(title)
        if (listName === null) return
        const safedSearchObj = await createSafedSearchObject({ searchifyFilters }, dataType, listName)
        if (index !== undefined) {
            field.value[index] = safedSearchObj
            onChangeIntercept(field.value)
            return
        }
        onChangeIntercept(field.value.concat(safedSearchObj))
    }

    const onEditPill = (selectedSearch: SafedSearch, index: number) => {
        openSearchify({
            dataType,
            onChange: (searchifyCallbackArgs) =>
                onChangeSearchify(searchifyCallbackArgs, index, selectedSearch.humanized_text),
            searchifyHeaderName: "Edit search",
            searchifyInitialFilters: {
                addedIds: selectedSearch.added_ids,
                deletedIds: selectedSearch.deleted_ids,
                querysetList: selectedSearch.filters,
            },
            defaultFilters,
        })
    }

    const handleEditSearchify = (pillData: SafedSearch) => {
        closeOfficialSelected(pillData)
        openSearchify({
            dataType,
            onChange: (searchifyCallbackArgs) =>
                onChangeSearchify(searchifyCallbackArgs, getPillGroupIndex, pillData.humanized_text),
            searchifyHeaderName: "Edit search",
            searchifyInitialFilters: {
                addedIds: pillData.added_ids,
                deletedIds: pillData.deleted_ids,
                querysetList: pillData.filters,
            },
            defaultFilters,
        })
    }

    const onEditCustomPill = (selectedSearch: SafedSearch, index: number): void => {
        setPillGroup(selectedSearch)
        setPillGroupIndex(index)
        setEditOfficialOpen(true)
    }

    const closeOfficialSelected = async (pillGroup: SafedSearch) => {
        const searchifyFilters = {
            addedIds: pillGroup.added_ids,
            deletedIds: pillGroup.deleted_ids,
            querysetList: pillGroup.filters,
            searchifyCount: pillGroup.estimated_size,
        }
        createSafedSearchObject({ searchifyFilters }, dataType, pillGroup.name).then((result) => {
            field.value[getPillGroupIndex] = result
            onChangeIntercept(field.value)
        })
        setEditOfficialOpen(false)
    }

    const countQuery = useQuery({
        queryKey: ["EditableSearchify", safedSearchIds],
        queryFn: fetchEstimatedSizes,
        staleTime: Infinity,
        enabled: !!safedSearchIds.length,
    })

    useEffect(() => {
        if (countQuery.isSuccess) {
            const data = countQuery.data
            const fieldsClone = [...field.value]

            fieldsClone.forEach((fieldItem) => {
                fieldItem.estimated_size = data?.[fieldItem.id] || 0
            })

            onChangeIntercept(fieldsClone)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countQuery.isSuccess])

    return (
        <Stack className={classes.stackWrapper}>
            <div className={classes.container}>
                <span className={classes.textLabel}>Recipients</span>
                <Group>
                    {field.value.length && (
                        <Button
                            className={classes.roundedButton}
                            variant="ghost"
                            color="gray"
                            size="xs"
                            onClick={() => onChangeIntercept([])}
                        >
                            Clear all
                        </Button>
                    )}
                    <Button
                        className={classes.roundedButton}
                        leftIcon={<Icon icon="plus" />}
                        variant="tonal"
                        color="gray"
                        size="xs"
                        onClick={() =>
                            openSearchify({
                                dataType,
                                onChange: onChangeSearchify,
                                searchifyHeaderName: "Custom search",
                                defaultFilters,
                            })
                        }
                    >
                        {buttonText}
                    </Button>
                </Group>
            </div>
            <Flex direction="row" gap="xs">
                {field.value.length ? (
                    <PillGroup
                        data={field.value}
                        onEdit={customEdit ? onEditCustomPill : onEditPill}
                        onClose={onChangeIntercept}
                        keyProp="id"
                        labelRender={(item) => {
                            if (countQuery.isLoading) {
                                return (
                                    <Flex align="center" gap="xs">
                                        {item.humanized_text} <Loader size="xs" />
                                    </Flex>
                                )
                            }

                            return `${item.humanized_text} (${countQuery.data?.[item.id] || 0})`
                        }}
                    />
                ) : (
                    <p className={classes.noRecipientsText}>No recipients selected</p>
                )}
            </Flex>
            {fieldState?.error && <ErrorMessages fieldError={fieldState?.error} />}
            {isEditOfficialOpen && (
                <EditOfficials
                    pillGroup={getPillGroup}
                    defaultFilters={defaultFilters}
                    isOpen={isEditOfficialOpen}
                    onClose={closeOfficialSelected}
                    onSearchifyEdit={handleEditSearchify}
                />
            )}
        </Stack>
    )
}

export const EditableSearchify = connect(null, { openSearchify })(DisconnectedEditableSearchify) as React.FC<
    ControlledComponentProps<SearchifyProps>
>
